import React, { useState } from 'react';
import { Download, FileText, Image as ImageIcon, Video, Briefcase, CheckCircle, ArrowRight, Layers } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';
import { Link } from 'react-router-dom';

interface Asset {
  id: string;
  name: string;
  type: 'pdf' | 'video' | 'image' | 'deck';
  size: string;
  description: string;
}

const ASSETS: Asset[] = [
  { id: '1', name: '2025 Sales Deck (Unbranded)', type: 'deck', size: '15.4 MB', description: 'PowerPoint presentation with placeholder logo areas for your agency branding.' },
  { id: '2', name: '360 Booth Hype Reel (No Watermark)', type: 'video', size: '45.2 MB', description: 'High-energy promotional video to show your clients what the experience looks like.' },
  { id: '3', name: 'Tech Spec Sheet & Floor Plans', type: 'pdf', size: '2.1 MB', description: 'Detailed dimensions and power requirements for venue managers.' },
  { id: '4', name: 'High-Res Image Pack', type: 'image', size: '128 MB', description: 'Curated gallery of 50+ event photos for use in your proposals.' },
  { id: '5', name: 'Pricing Calculator Spreadsheet', type: 'deck', size: '1.2 MB', description: 'Internal tool to calculate your margin and markup.' },
  { id: '6', name: 'Email Outreach Templates', type: 'pdf', size: '0.5 MB', description: 'Copy-paste email scripts for pitching corporate clients.' },
];

export const PartnerPortal = ({ onOpenQuote }: { onOpenQuote: () => void }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulation of login
    if (email && password) {
      setIsAuthenticated(true);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-brand-neonBlue/5 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-brand-neonPink/5 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="w-full max-w-md relative z-10 animate-fade-in-up">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-800 text-brand-neonBlue mb-4">
              <Briefcase size={24} />
            </div>
            <h1 className="text-3xl font-display font-bold text-white">Agency Partner Hub</h1>
            <p className="text-slate-400 mt-2">Access white-label sales materials to resell our services.</p>
          </div>

          <GlassCard className="border-t border-white/20 shadow-2xl">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Agency Email</label>
                <input
                  type="email"
                  required
                  className="block w-full bg-slate-900/50 border border-slate-700 rounded-lg py-3 px-4 text-white placeholder-slate-500 focus:ring-brand-neonBlue focus:border-brand-neonBlue outline-none transition-colors"
                  placeholder="planner@agency.co.nz"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Partner ID</label>
                <input
                  type="password"
                  required
                  className="block w-full bg-slate-900/50 border border-slate-700 rounded-lg py-3 px-4 text-white placeholder-slate-500 focus:ring-brand-neonBlue focus:border-brand-neonBlue outline-none transition-colors"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <Button type="submit" className="w-full">
                Access Resources <ArrowRight size={18} className="ml-2" />
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-slate-400 mb-3">Don't have a partner ID?</p>
              <button onClick={onOpenQuote} className="text-brand-neonBlue hover:text-white text-sm font-bold transition-colors">
                Apply to become a Reseller
              </button>
            </div>
          </GlassCard>
        </div>
      </div>
    );
  }

  // Dashboard View
  return (
    <div className="min-h-screen pt-24 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Dashboard Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12 animate-fade-in-up">
          <div>
            <div className="flex items-center gap-2 text-brand-neonBlue mb-2">
              <CheckCircle size={16} />
              <span className="text-xs font-bold uppercase tracking-widest">Verified Partner Access</span>
            </div>
            <h1 className="text-4xl font-display font-bold text-white">Marketing Resource Centre</h1>
            <p className="text-slate-400 mt-2 max-w-2xl">
              Download unbranded assets to customize for your client presentations. All files are white-label and ready for your agency logo.
            </p>
          </div>
          <div className="flex gap-4">
             <Button variant="outline" onClick={() => setIsAuthenticated(false)}>Log Out</Button>
             <Button onClick={onOpenQuote}>Request Custom Quote</Button>
          </div>
        </div>

        {/* Categories */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
           <GlassCard className="p-6 bg-brand-neonBlue/5 border-brand-neonBlue/20">
              <h3 className="text-xl font-bold text-white mb-2">Reseller Commission</h3>
              <div className="text-3xl font-display font-bold text-brand-neonBlue mb-1">15 - 20%</div>
              <p className="text-sm text-slate-400">Standard agency discount applied to all bookings made through this portal.</p>
           </GlassCard>
           
           {/* NEW: Mockup Tool Link */}
           <Link to="/mockup-studio" className="block">
             <GlassCard className="p-6 h-full border-brand-neonPink/30 hover:bg-white/10 hover:scale-[1.02] transition-all">
                <div className="flex items-center gap-2 text-brand-neonPink mb-3">
                  <Layers size={24} />
                  <h3 className="text-xl font-bold text-white">Mockup Studio</h3>
                </div>
                <div className="text-slate-300 text-sm mb-4">
                  Create instant visual mockups for your clients. Upload their logo and see it on our booths.
                </div>
                <span className="text-brand-neonPink text-sm font-bold flex items-center gap-1">
                  Launch Tool <ArrowRight size={14} />
                </span>
             </GlassCard>
           </Link>

           <GlassCard className="p-6">
              <h3 className="text-xl font-bold text-white mb-2">Priority Support</h3>
              <div className="text-slate-300 text-sm mb-4">Direct line to our operations manager for urgent client questions.</div>
              <a href="mailto:partners@quikpix.co.nz" className="text-brand-neonPink hover:text-white text-sm font-bold">Email Priority Support &rarr;</a>
           </GlassCard>
        </div>

        {/* Assets Grid */}
        <h2 className="text-2xl font-display font-bold text-white mb-6">Downloadable Assets</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {ASSETS.map((asset) => (
            <GlassCard key={asset.id} hoverEffect className="group flex flex-col h-full relative overflow-hidden">
               <div className="flex items-start justify-between mb-4">
                 <div className="p-3 rounded-lg bg-slate-800 text-brand-neonBlue group-hover:bg-brand-neonBlue group-hover:text-slate-900 transition-colors">
                    {asset.type === 'pdf' && <FileText size={24} />}
                    {asset.type === 'deck' && <Briefcase size={24} />}
                    {asset.type === 'video' && <Video size={24} />}
                    {asset.type === 'image' && <ImageIcon size={24} />}
                 </div>
                 <span className="text-xs font-mono text-slate-500 bg-slate-900 px-2 py-1 rounded border border-white/5">
                   {asset.size}
                 </span>
               </div>
               
               <h3 className="text-lg font-bold text-white mb-2">{asset.name}</h3>
               <p className="text-sm text-slate-400 mb-6 flex-1 leading-relaxed">
                 {asset.description}
               </p>

               <button className="w-full py-3 rounded-lg border border-white/10 bg-white/5 text-white hover:bg-brand-neonBlue hover:border-brand-neonBlue hover:text-slate-900 transition-all font-medium flex items-center justify-center gap-2">
                 <Download size={18} /> Download Asset
               </button>
            </GlassCard>
          ))}
        </div>
        
      </div>
    </div>
  );
};
